import {
  Feather,
  FontAwesome,
  Ionicons,
  MaterialCommunityIcons,
  Octicons,
  SimpleLineIcons,
} from '@expo/vector-icons'
import { type ComponentType } from 'react'
import { Text, type ColorValue, type StyleProp, type TextStyle } from 'react-native'
import { cn } from '~/lib/utils.ts'
// @ts-ignore
import Logo from '~/media/aikoa-logo-sand.svg'

export type CustomIconNames = 'aikoa'

export type IoniconName = keyof typeof Ionicons.glyphMap
export type MaterialCommunityIconName = keyof typeof MaterialCommunityIcons.glyphMap

export type IconName =
  | IoniconName
  | keyof typeof Feather.glyphMap
  | keyof typeof Octicons.glyphMap
  | keyof typeof SimpleLineIcons.glyphMap
  | MaterialCommunityIconName
  | keyof typeof FontAwesome.glyphMap
  | CustomIconNames

export type IconLibrary =
  | 'ionicons'
  | 'feather'
  | 'octicons'
  | 'simplelineicons'
  | 'materialcommunityicons'
  | 'fontawesome'

export type IconNameProps =
  | {
      library: 'ionicons'
      name: IoniconName | CustomIconNames
    }
  | {
      library: 'feather'
      name: keyof typeof Feather.glyphMap | CustomIconNames
    }
  | {
      library: 'octicons'
      name: keyof typeof Octicons.glyphMap | CustomIconNames
    }
  | {
      library: 'simplelineicons'
      name: keyof typeof SimpleLineIcons.glyphMap | CustomIconNames
    }
  | {
      library: 'materialcommunityicons'
      name: MaterialCommunityIconName | CustomIconNames
    }
  | {
      library: 'fontawesome'
      name: keyof typeof FontAwesome.glyphMap | CustomIconNames
    }
  | {
      library?: undefined
      name: IconName
    }

export type IconProps = {
  size?: number | string
  width?: number | string
  height?: number | string
  className?: string
  color?: ColorValue
} & IconNameProps

type VectorIconProps<GlyphName extends string> = {
  name: GlyphName
  size?: number
  color?: ColorValue
  style?: StyleProp<TextStyle>
}

type VectorIconComponent<GlyphName extends string> = ComponentType<VectorIconProps<GlyphName>>

function getResolvedIconSize(size: number | string | undefined) {
  if (typeof size === 'number') {
    return size
  }

  if (typeof size === 'string') {
    const parsedSize = Number(size)

    if (Number.isFinite(parsedSize)) {
      return parsedSize
    }
  }

  return undefined
}

function renderVectorIcon<GlyphName extends string>({
  component: Component,
  name,
  size,
  color,
  className,
}: {
  component: VectorIconComponent<GlyphName>
  name: GlyphName
  size: number | string | undefined
  color: ColorValue | undefined
  className: string | undefined
}) {
  return (
    <Text className={cn(className)}>
      <Component name={name} size={getResolvedIconSize(size)} color={color} />
    </Text>
  )
}

export default function Icon({
  name,
  size,
  width = size,
  height = size,
  className,
  color,
  library,
}: IconProps) {
  if ((!library || library === 'ionicons') && name in Ionicons.glyphMap) {
    return renderVectorIcon({
      component: Ionicons,
      name: name as keyof typeof Ionicons.glyphMap,
      size,
      color,
      className,
    })
  }

  if ((!library || library === 'feather') && name in Feather.glyphMap) {
    return renderVectorIcon({
      component: Feather,
      name: name as keyof typeof Feather.glyphMap,
      size,
      color,
      className,
    })
  }

  if ((!library || library === 'octicons') && name in Octicons.glyphMap) {
    return renderVectorIcon({
      component: Octicons,
      name: name as keyof typeof Octicons.glyphMap,
      size,
      color,
      className,
    })
  }

  if ((!library || library === 'simplelineicons') && name in SimpleLineIcons.glyphMap) {
    return renderVectorIcon({
      component: SimpleLineIcons,
      name: name as keyof typeof SimpleLineIcons.glyphMap,
      size,
      color,
      className,
    })
  }

  if (
    (!library || library === 'materialcommunityicons') &&
    name in MaterialCommunityIcons.glyphMap
  ) {
    return renderVectorIcon({
      component: MaterialCommunityIcons,
      name: name as keyof typeof MaterialCommunityIcons.glyphMap,
      size,
      color,
      className,
    })
  }

  if ((!library || library === 'fontawesome') && name in FontAwesome.glyphMap) {
    return renderVectorIcon({
      component: FontAwesome,
      name: name as keyof typeof FontAwesome.glyphMap,
      size,
      color,
      className,
    })
  }

  return <Logo width={width} height={height} fill={color} className={cn(className)} />
}
